/**
  * @class meleInput
  * @description
  *
  * # Purpose
  * Implements `<mele-input>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-input></mele-input>';
  * ```
  * Create element in JavaScript:
  * ```
  * const el = elParent.appendChildren({
  *   tag: 'mele-input',
  *   type: 'string',
  *   label: 'Enter value:',
  *   value: 'Sample value',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  * There are these states of the element:
  * - readonly; user cannot change value, value is presented in textual form
  * - disabled; user cannot change value; value is presented in editable form but dimmed
  * - editable; user can edit the field but is not doing now; value is presented in editable form
  */

import meleItem from 'meleItem'
const geo = require('msysGeo')
const moment = require('moment')
const TIMEFORMAT = {
  datetime: 'DD.MM.YYYY HH:mm',
  datetimems: 'DD.MM.YYYY HH:mm:ss',
  datetimeus: 'DD.MM.YYYY HH:mm:ss',
  date: 'DD.MM.YYYY',
  datems: 'DD.MM.YYYY',
  time: 'HH:mm',
  timems: 'HH:mm:ss',
  hm: 'HH:mm',
  hms: 'HH:mm:ss',
  datehm: 'DD.MM.YYYY HH:mm',
  datehms: 'DD.MM.YYYY HH:mm:ss',
  'date?hm': 'DD.MM.YYYY HH:mm',
  'date?hms': 'DD.MM.YYYY HH:mm:ss',
}
const Chart = require('chart.js')

// rule, change, flag, matchrule
const TRANSFORM_TEMPLATES = {
  pnc: '^(\\d{6})(\\d+)$∫$1/$2∫i∫^[\\/0-9]+$',
  // transform YYYY-MM-DD to DD.MM.YYYY
  ddmmyyyy: '^(\\d{4})-(\\d{2})-(\\d{2})$∫$3.$2.$1∫i',
  // "birthdate": "^(0?[1-9]|[12]\\d|3[01])(0?[1-9]|1[012])(\\d{4})$∫$1.$2.$3∫i"
}
const VALIDATION_TEMPLATES = {
  birthdate: '^(?:(?:31(\\.)(?:0?[13578]|1[02]))\\1|(?:(?:29|30)(\\.)(?:0?[1,3-9]|1[0-2])\\2))(?:(?:1[6-9]|[2-9]\\d)?\\d{4})$|^(?:29(\\.)0?2\\3(?:(?:(?:1[6-9]|[2-9]\\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\\d|2[0-8])(\\.)(?:(?:0?[1-9])|(?:1[0-2]))\\4(?:(?:1[6-9]|[2-9]\\d)?\\d{4})$',
  integer: '^[+-]?\\d+$',
  einteger: '^([+-]?\\d+)?$',
  natural: '^\\d+$',
  pinteger: '^[1-9]\\d*$',
  float: '^[+-]?\\d+(\\.\\d*)?$',
  efloat: '^([+-]?\\d+(\\.\\d*)?)?$',
  pfloat: '^\\d+(\\.\\d*)?$',
  string: '^\\D+?$',
  pnc: '^\\d{6}\\/\\d{3,4}$',
  year: '^(18|19|20|21)\\d\\d$',
  currency: '^\\d+(\\.\\d\\d?)?$',
  url: '^\\w+://.+$',
  eurl: '^(\\w+://.+)?$',
  email: '^[\\w\\.\\-]+@[\\w\\-]+(\\.[\\w\\-]+)+$',
  eemail: '^([\\w\\.\\-]+@[\\w\\-]+(\\.[\\w\\-]+)+)?$',
  phone: '^(\\+?\\d+|\\(\\+?\\d+([ .\\/-]\\d+)?\\)|\\+\\(\\d+([ .\\/-]\\d+)?\\))([ \\/.-]?\\d+|\\s*\\(\\d+\\)\\s*)*$',
  ephone: '^((\\+?\\d+|\\(\\+?\\d+([ .\\/-]\\d+)?\\)|\\+\\(\\d+([ .\\/-]\\d+)?\\))([ \\/.-]?\\d+|\\s*\\(\\d+\\)\\s*)*)?$',
  notblank: '\\S',
  date: '^(?:(?:31(\\.)(?:0?[13578]|1[02]))\\1|(?:(?:29|30)(\\.)(?:0?[1,3-9]|1[0-2])\\2))(?:(?:1[6-9]|[2-9]\\d)?\\d{4})$|^(?:29(\\.)0?2\\3(?:(?:(?:1[6-9]|[2-9]\\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\\d|2[0-8])(\\.)(?:(?:0?[1-9])|(?:1[0-2]))\\4(?:(?:1[6-9]|[2-9]\\d)?\\d{4})$',
  datehm: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-5]?\\d$',
  datetime: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-59]\\d$',
  datetimems: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-59]\\d:[0-59]\\d(\\.\\d{0,3})?$',
  datetimeus: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-59]\\d:[0-59]\\d(\\.\\d{0,6})?$',
  datetime_us: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-59]\\d:[0-59]\\d(\\.\\d{0,6})?$',
  'date?hm': '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d( ([01]?\\d|2[0-3]):[0-5]?\\d)?$',
  'date?hms': '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d( ([01]?\\d|2[0-3]):[0-59]\\d:[0-59]\\d)?$',
  datehms: '^(0?[1-9]|[12]\\d|3[01])\\.(0?[1-9]|1[012])\\.(20|19)\\d\\d ([01]?\\d|2[0-3]):[0-59]\\d:[0-59]\\d$',
  timems: '^([01]?\\d|2[0-3]):[0-5]?\\d:[0-5]+?\\d[0-9]$',
  hm: '^([01]?\\d|2[0-3]):([0-5]+?\\d)$',
  hms: '^([01]?\\d|2[0-3]):[0-5]?\\d:[0-5]+?\\d$',
  hhmm: '^([01]\\d|2[0-3]):[0-5]+\\d$',
  extended: '\\S',
}

const PROPERTIES = _.extend({}, {
  type: {
    type: 'string',
    description: 'type of the field',
    enum: [
      'empty', 'invisible',
      // textual
      'string', 'password', 'text', 'richtext',
      // boolean
      'check', 'yesno', 'checkmark',
      // numeric
      'slider', 'range', 'progress', 'rotary', 'rotarystring',
      // temporary options, with updated rendering
      'slist', 'slookup', 'sdropdown',
      'autocomplete',
      // options
      'list', 'radio', 'group', 'icongroup', 'grid', 'ovals', 'tags', 'dropdown', 'lookup', 'combo', 'users',
      // attachments
      'file', 'image', 'images', 'signature', 'icon', 'files', 'attachments',
      // geo
      'geohash', 'geoshape', 'latlng',
      // date and time
      'date', 'datems', 'datetime', 'datetimems', 'datetimeus', 'datetime_us', 'time', 'timeus', 'time_us', 'duration', 'durationms',
      // graph
      'graph',
      // input with value rendered by a custom script
      'custom',
      // special types
      'keyvalue',
    ],
  },
  inputmode: { type: 'string', description: 'inputmode attribute for input element', enum: ['none', 'text', 'decimal', 'numeric', 'tel', 'email', 'url', 'search'] },
  rows: { type: 'number', description: 'Limits number of visible rows in textarea and list-based inputs' },
  steps: { type: 'array', description: 'sets size for one or multiple steps for decrease and increase ranged input type' },
  design: { type: 'string', description: 'determines style of individual item for multivalued types', enum: ['none', 'group', 'oval', 'block', 'grid', 'tag', 'toolbar'] },
  multi: { type: 'boolean', description: 'if true, allows multiple selected items (for appropriate types only)' },
  readonly: { type: 'boolean', description: 'Defines if the element acts as a getter or a setter' },
  localize: { type: 'boolean', description: 'Defines if the value attribute created at input should be translated' },
  value: { type: 'string', description: 'value of the input; multiple values are separated by ∫' },
  initialvalue: { type: 'stringornull', description: 'initial value before user changes; set automatically on user interaction, cleared when value set programmatically' },
  servervalue: { type: 'stringornull', description: 'current value on server; if not set, initial value resp. value is expected to be the server version' },
  placeholder: { type: 'string', description: 'Placeholder text for string fields to be displayed while value is not specified' },
  help: { type: 'string', description: 'Helper text for the field (on separate line)' },
  before: { type: 'string', description: 'Helper text before field' },
  after: { type: 'string', description: 'Helper text after field' },
  format: { type: 'string', description: 'Defines regexp for format check of the input' },
  auto: { type: 'boolean', description: 'if set, will automatically populate options with trnaslation terms starting with value of prefix attribute' },
  options: { type: 'array', description: 'Sets the list of options available for selection if type attribute is set to checkbox, radio or select, separated by ∫ symbol' },
  itemlabel: { type: 'stringornull', description: 'label used for all input options' },
  itemlabel2: { type: 'stringornull', description: 'sublabel used for all input options' },
  itemvalue: { type: 'stringornull', description: 'value used for all input options' },
  itemcolor: { type: 'stringornull', description: 'color used for all input options' },
  itemicon: { type: 'stringornull', description: 'icon used for all input options' },
  blank: { type: 'boolean', description: 'Defines if an empty value is allowed' },
  submitbutton: { type: 'string', description: "on submit event will find a button a call it's action" },
  sort: { type: 'array', description: 'list of property+ or property- indicates sorting criteria; $ suffix after property name indicates sorting over translation of the property, @ suffix means numeric sorting' },
  formaterror: { type: 'string', description: 'Error msg if validation failes', default: '' },
  conflict: { type: 'boolean', description: 'Indicates value, initial value and servervalue are different', readonly: true },
  split: { type: 'string', description: 'Sets the split of inner parts of the element, i.e of label and value. Options for split: 1:1, 1:2, 1:3, 1:4, 2:3, 3:4' },
  dynamicwidth: { type: 'number', description: 'Extends width to next posible based on current value length' },
  linkmod: { type: 'string', description: 'Defines which module can show details of the item' },
  linkid: { type: 'string', description: 'Defines which id shows details of the item; if not set, value is used by default' },
  invalid: { type: 'boolean', description: 'Set automatically if validation fails', readonly: true },
  changed: { type: 'boolean', description: 'Set automatically if user interaction changed the field', readonly: true },
  eventValueChanged: { type: 'function', description: 'Called when value change; if not set, will publish mmValueChanged message to parent module' },
  eventValueSubmitted: { type: 'function', description: 'Called when value was submitted by Enter; if not set, will call formSubmit on holder' },
  eventSearch: { type: 'function', description: 'method that can be overwritten to provide custom search results in lists and geoshapes' },
  focused: { type: 'boolean', description: 'set a flag if input is focused by user' },
  transform: { type: 'array', description: 'defines in which input value should be transformed. accepts two values, separated by ∫ symbol. First sets the gui transformation(uppercase, lowercase, capitalize) and second part formats the value' },
  min: { type: 'number', description: 'lowest allowed value' },
  max: { type: 'number', description: 'highest allowed value' },
  radian: { type: 'number', description: 'value of one rotation for rotary input' },
  round: { type: 'number', description: 'rounding of rotary value', default: 1 },
  minor: { type: 'number', description: 'value between minor ticks' },
  major: { type: 'number', description: 'value between major ticks' },
  maxlength: { type: 'number', description: 'maximum allowed characters in input' },
  height: { type: 'number', description: 'height of image value' },
  show: { type: 'array', description: 'shows element, if array content is evaluated to true' },
  rounding: { type: 'string', description: 'rounds time in dt type of input', enum: ['start', 'now', 'end'], default: 'now' },
  toolbar: { type: 'string', description: 'sets the toolbar of wysiwyg' },
  tabindex: { type: 'number', description: 'sets tab index of dropdown' },
  searchbar: { type: 'boolean', description: 'whether to show search bar in geoshape' },
  editing: { type: 'boolean', description: 'true when user is just editing the value using a popup' },
  follow: { type: 'boolean', description: 'if true, rotary change will immediately change underlying value' },
  cal_mode: { type: 'string', description: 'mode for datetime calendar; empty will display traditional calendar, strict displays player calendar allowing selection of marked times only, easy allows any time', enum: ['', 'strict', 'easy'], default: '' },
  cal_source: { type: 'string', description: 'source of streaming for calendar', default: 'SERVER@msysStreamer' },
  cal_indexes: { type: 'array', description: 'list of player mm indices to be used in datetime calendar', default: 'h264' },
  cal_unit: { type: 'string', description: 'initial unit selected in datetime calendar', enum: ['year', 'month', 'day', 'hour', 'minute', 'second'], default: 'day' },
  cal_lastunit: { type: 'string', description: 'last displayed and selectable unit in datetime calendar', enum: ['year', 'month', 'day', 'hour', 'minute', 'second'], default: 'second' },
  mime: { type: 'string', description: 'Allowed mime types for file upload', default: 'image/*' },
  rendertype: { type: 'string', description: 'Defines the type of elements layout', enum: ['grid', 'list'], default: 'grid' },
}, meleItem._properties)

let mele_value_template
let clear_icon_template

export default class meleInput extends meleItem {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    if(!mele_value_template) {
      mele_value_template = this.createElementFast('mele-value')
      mele_value_template.innerHTML = '<mele-value-wrapper><mele-value-content><div></div></mele-value-content></mele-value-wrapper>'
      clear_icon_template = this.ownerDocument.getElementById('icons').content.getElementById('u.clear').cloneNode(true)
      clear_icon_template.setAttribute('class', 'image-clear')
      clear_icon_template.removeAttribute('id')
    }
    super.init()
    this._files = {}
    this.forAll('>mele-icon.remove', el=>el.deleteElement())

    this._elValue = this.find('>mele-value')
    if(!this._elValue) {
      this._elValue = mele_value_template.cloneNode(true)
      this._elValue.classList.add(this.classScope('value'))
      this.appendChild(this._elValue)
    }else{
      this._elValue.innerHTML = '<mele-value-wrapper><mele-value-content><div></div></mele-value-content></mele-value-wrapper>'
    }

    this._elValueWrapper = this._elValue.children[0]
    this._elValueContent = this._elValueWrapper.children[0]
    this._elValueElement = this._elValueContent.children[0]

    this._elValueContent.onscroll = this.valueContentOnScroll.bind(this)
    this._elValueContent.onscrollend = this.valueContentOnScrollEnd.bind(this)
    this._elValueContent.onclick = this.valueContentOnClick.bind(this)
  } // init

  valueContentOnScroll() {
    if(this.scrollEndTimer) clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = setTimeout(this.valueContentOnScrollEnd.bind(this), 100)
  }

  valueContentOnScrollEnd() {
    if(this.type==='slider') {
      const slider = this._elValueContent
      const list = slider.find('>mele-list')
      const items = list.getItemElements()
      if(!slider || items.length===0) return
      const slider_scroll = slider.scrollLeft
      for(let i=1; i<items.length; i++) {
        let item = items[i]
        if(item.offsetLeft>=slider_scroll) {
          const prev_item = items[i-1]
          if(slider.scrollLeft-prev_item.offsetLeft < item.offsetLeft-slider.scrollLeft) { item = prev_item }
          this.value = item.key
          slider.scrollLeft = item.offsetLeft
          return
        }
      }
      const item = items[items.length-1]
      this.value = item.key
      slider.scrollLeft = item.offsetLeft
    }
  }

  valueContentOnClick() {
    if(this.linkmod && (this.readonly || this.type==='custom')) {
      const mod = /{/.test(this.linkmod) ? this.translate(this.linkmod, true) : this.linkmod
      let id = _.toString(this.linkid)
      id = id==='' ? _.toString(this.value) : _.toString(/{/.test(id) ? this.translate(id, false) : id)
      if(id!=='') this.publishOpen(mod, id)
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Method that creates rotary element able to change value by rotating finger/mouse around
  */
  startRotary(e) {
    // prevent long press for context menu
    e.preventDefault()
    this.editing = true

    const pos = ({ pageX, pageY, touches })=>{
      return touches ? { x: touches?.[0]?.pageX, y: touches?.[0]?.pageY } :
        { x: pageX, y: pageY }
    } // pos

    // open modal window with controls
    const elModal = this.modalOpen({
      key: 'modal_'+this.type,
      children: [{ tag: 'rotary' }],
    })

    const elRotary = elModal.find('rotary')
    elRotary.classList.add(this.classScope('rotary'))
    const [elHeader, elAction] = elRotary.appendChildren([
      { tag: 'header' },
      { tag: 'action' },
    ])
    elAction.classList.add(this.classScope('action'))
    const [elClear, elVal, elCancel, elTicks, elPointer] = elAction.appendChildren([
      { tag: 'mele-text', label: 'form.clear' },
      { tag: 'val' },
      { tag: 'mele-text', label: 'form.cancel' },
      { tag: 'svg', class: 'ticks' },
      { tag: 'svg', class: 'pointer' },
    ])

    const generateLines = (width, start, end, count, attrs)=>{
      const center = width / 2
      let lines = ''
      const unitAngle = 2 * Math.PI / count
      for(let i = 0; i < count; i++) {
        const angle = i * unitAngle
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)
        const x1 = center + start * cos
        const y1 = center + start * sin
        const x2 = center + end * cos
        const y2 = center + end * sin
        lines += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" ${attrs} />`
      }
      return lines
    } // generateLines

    // get element configuration values
    const minValue = _.ifNaN(this.min, 0)
    const maxValue = _.ifNaN(this.max, 100)
    const radianValue = _.ifNaN(this.radian, 100)
    const minorTick = _.ifNaN(this.minor, 1)
    const majorTick = _.ifNaN(this.major, 10)
    const rounding = parseFloat(this.round) || 1

    elTicks.setAttribute('viewBox', '0 0 100 100')
    elTicks.innerHTML =
      (minorTick>0 ? generateLines(100, 41, 45, Math.round(radianValue/minorTick), 'stroke="gray" stroke-width="1"') : '') +
      (majorTick>0 ? generateLines(100, 38, 45, Math.round(radianValue/majorTick), 'stroke="black" stroke-width="1"') : '') +
      generateLines(100, 35, 45, 4, 'stroke="black" stroke-width="2"') +
      generateLines(100, 30, 50, 1, 'stroke="black" stroke-width="5"')

    elPointer.setAttribute('viewBox', '0 0 100 100')
    elPointer.innerHTML = generateLines(100, 5, 50, 1, 'stroke="red" stroke-width="1"')

    // get initial value
    const initialValue = _.ifNaN(this.value, minValue)
    elHeader.innerHTML = initialValue

    // reposition rotary - ideal is that mousedown pos is at mid-top of rotary
    const { x, y } = pos(e) // position of mousedown/touchstart
    const max_x = window.innerWidth
    const max_y = window.innerHeight
    let rect = elRotary.getBoundingClientRect()
    let rotary_left = x - rect.width/2
    if(rotary_left<0) rotary_left=0
    if(rotary_left+rect.width>max_x) rotary_left = max_x - rect.width
    let rotary_top = y - 100
    if(rotary_top<0) rotary_top=0
    if(rotary_top+rect.height>max_y) rotary_top = max_y - rect.height
    elRotary.style.top = rotary_top+'px'
    elRotary.style.left = rotary_left+'px'

    // calculate center of circle
    rect = elAction.getBoundingClientRect()
    const cx = rect.left + window.scrollX + rect.width / 2
    const cy = rect.top + window.scrollY + rect.height / 2
    const radius = rect.height / 2

    // helper functions
    const angle = (x, y)=>{
      const dx = x - cx
      const dy = y - cy
      const angle = Math.atan2(dy, dx) * 180 / Math.PI
      return angle<0 ? angle+360 : angle
    } // angle

    const distance = (x, y)=>{
      const dx = x - cx
      const dy = y - cy
      return Math.sqrt(dx*dx+dy*dy)
    } // distance

    // calculate additional initial values
    const initialAngle = angle(x, y)
    let lastAngle = initialAngle
    let totalAngle = 0

    // rotate SVG ticks appropriately
    const zeroAngle = initialAngle - initialValue/radianValue*360
    elTicks.style.transform = `translate(-50%, -50%) rotate(${zeroAngle}deg)`

    // method handling moving of the mouse/finger
    const onMove = (e)=>{
      e.preventDefault()
      const { x, y } = pos(e)
      const dist = distance(x, y)

      elCancel.className = ''
      elClear.className = ''

      if(dist>radius) {
        // out of circle
        if(x < cx) {
          // left to circle
          elClear.className = 'hover'
          elHeader.innerHTML = ''
        }else if(x > cx) {
          // right to circle
          elCancel.className = 'hover'
          elHeader.innerHTML = initialValue
        }
      }else{
        // inside circle
        const newAngle = angle(x, y)
        let diff = newAngle - lastAngle
        diff = diff > 180 ? diff - 360 : diff < -180 ? diff + 360 : diff
        totalAngle += diff
        lastAngle = newAngle
        let newValue = initialValue + totalAngle/360*radianValue
        newValue = minValue>newValue ? minValue : maxValue<newValue ? maxValue : newValue
        elHeader.innerHTML = _.roundBy(Math.round(newValue/rounding)*rounding, 6)
        elPointer.style.transform = `translate(-50%, -50%) rotate(${lastAngle}deg)`
      }
      if(this.follow) {
        this.setProperty('value', elHeader.innerHTML, true)
      }
    } // onMove

    // method handling releasing of mouse button/finger
    const onEnd = (e)=>{
      this.setProperty('value', elHeader.innerHTML, true)
      elModal.removeEventListener('mousemove', onMove)
      elModal.removeEventListener('mouseend', onEnd)
      elModal.removeEventListener('pointermove', onMove)
      elModal.removeEventListener('pointerup', onEnd)
      this.removeEventListener('touchmove', onMove)
      this.removeEventListener('touchend', onEnd)
      elModal.deleteElement()
      this.editing = false
    } // onEnd

    // setup events for move and end
    elModal.addEventListener('mousemove', onMove, { passive: false })
    elModal.addEventListener('mouseup', onEnd, { passive: false })
    elModal.addEventListener('pointermove', onMove, { passive: false })
    elModal.addEventListener('pointerup', onEnd, { passive: false })
    this.addEventListener('touchmove', onMove, { passive: false })
    this.addEventListener('touchend', onEnd, { passive: false })

    // testing events
    // const mouseEvents = ['click', 'dblclick', 'mousedown',  'mouseup', 'mousemove',  'mouseover', 'mouseout',   'mouseenter', 'mouseleave', 'contextmenu']
    // const pointerEvents = ['pointerdown', 'pointerup', 'pointermove', 'pointerover', 'pointerout', 'pointerenter', 'pointerleave', 'pointercancel', 'gotpointercapture', 'lostpointercapture']
    // const touchEvents = ['touchstart', 'touchend', 'touchmove', 'touchcancel']
    // for(let p of [...mouseEvents, ...pointerEvents, ...touchEvents]) {
    //   elModal.addEventListener(p, (e)=>{
    //     const {x, y} = pos(e)
    //     console.log2('EVENTING MODAL', p, {x, y})
    //   })
    // }
  } // startRotary

  toolbarChanged(value) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handler used to submit value on Enter key press
  */
  handleKeyPress = (e)=>{
    if(this.disabled) return
    if(e.shiftKey) return
    if(this.format === 'json') return
    const keyCode = e.keyCode || e.which
    if(keyCode !== 13 && keyCode !== 10) return

    e.preventDefault()

    // shake when Enter on invalid input and abort
    if(this.invalid) {
      this.classList.add('shake')
      setTimeout(()=>{
        this.classList.remove('shake')
      }, 500)
      return
    }

    const holder = this.closest('.mele-container:not([propagate])')
    if(holder && !holder.invalid) {
      if(typeof this.eventValueSubmitted === 'function') {
        this.eventValueSubmitted(this, this.value)
      } else if(typeof (holder.formSubmit)==='function') {
        if(this.submitbutton) {
          const elButton = holder.find('@{'+this.submitbutton+'}')
          if(elButton && elButton.action) {
            const action = 'form'+_.capitalize(elButton.action.replace(/\W/g, ''))
            if(typeof (holder[action])==='function') {
              return holder[action](elButton)
            }
          }
        } else {
          holder.formSubmit()
        }
      }
    }
  } // handleKeyPress

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Changed type of input. Refresh of the value representation is needed.
  */

  typeChanged() {
    const type=this.type

    // delete previous editor

    const me=this

    const handleKeyPress = (e)=>{
      this.handleKeyPress(e)
    }

    // configure properties for updated editing element
    const editor = {
      key: this.type+'_value:'+this.key,
    }

    if(this._elClear) this._elClear = this._elClear.deleteElement()
    if(this._elEdit) this._elEdit = this._elEdit.deleteElement()
    this._elValueContent.forAll('>@geochange,>@geolegend', el=>el.deleteElement())
    this._elLegend = null

    if(type==='attachments') {
      _.extend(editor, {
        tag: 'mele-attachments',
        readonly: this.readonly,
        rendertype: this.rendertype || PROPERTIES.rendertype?.default,
        files: this.value,
      })
    }else if(type==='icon') {
      _.extend(editor, {
        tag: 'mele-image',
      })
    }else if(type=== 'autocomplete') {
      _.extend(editor, {
        tag: 'auto-input',
        contenteditable: true,
        class: 'editfield',
        size: 1,
        type: 'text',
        oninput: (e)=>{
          // find our bold container
          const value = findOption(e)
          function findOption(e) {
            if(!e.isTrusted) {
              e.data = e.target.value
            }
            const nnode = e.target.querySelector('strong') || document.createElement('strong')
            // get the content of the container, or input value
            const input = !e.isTrusted || nnode.innerHTML.length === 0 ? e.data : nnode.innerHTML
            // this will create a selection
            const sel = window.getSelection()
            const range = sel.getRangeAt(0)
            // clear up content of input
            e.target.innerText = ''

            // if we didn't get a input, clear content and leave
            if(!input) return ''

            // insert the node into range, or select the node
            range.surroundContents(nnode)
            // put the content
            nnode.innerHTML = me.doTransformation(input)
            // find match
            const result = me._trOptions.find(el=>{
              if(!input.match(/\w/)) return null
              const reg = new RegExp('^'+input.toUpperCase(), 'g')
              return reg.test(el.toUpperCase())
            })
            // append content after bold text
            if(result) {
              e.target.appendChild(document.createTextNode(result.substring(input.length)))
            }
            // set cursor to last char written
            range.collapse(false)
            const idx = me._trOptions.indexOf(result)
            return me._options[idx]
          }
          me.setProperty('value', value, true)
        },
        onfocus: (e)=>{
          if(this.focusFromMethod) {
            // reset vars
            this.focusFromMethod = false
            // internal focus() will hapen, no need to go into our method
          } else {
            // internal focus() happend, but we still need to send messages
            this.inputFocus(false, true)
          }
        },
        onblur: ()=>{
          // internal blur just happend

          if(this.fromKeyboard) {
            // reset vars
            this.fromKeyboard = false
            // event came from keyboard, so we need to refocus, no need for messages
            this.focusFromMethod = true
            this._elValueElement.focus()
          } else {
            this.focusedChanged(false)
          }
        },
        onkeypress: handleKeyPress.bind(me),
      })
      this._elValueContent.classList.add(this.classScope('content'))
      this._elClear = this._elValueContent.appendChildren({
        tag: 'mele-button',
        type: 'iconmini',
        key: 'clear',
        iconleft: 'u.clear',
        eventButtonClicked: ()=>{},
        onclick: ()=>{ this.value=''; if(this.format !== 'extended') this.find('.editfield').focus() },
      })
    }else if(type==='graph') {
      _.extend(editor, {
        tag: 'canvas',
      })
    }else if((this.readonly && type==='signature') || type==='signature') {
      _.extend(editor, {
        tag: 'mele-signature',
      })
      if(!this.readonly) {
        this._elValueContent.classList.add(this.classScope('content'))
        this._elEdit = this._elValueContent.appendChildren({
          tag: 'mele-button',
          type: 'iconmini',
          key: 'edit',
          iconleft: 'u.edit',
          eventButtonClicked: ()=>{
            this._elValueElement.showSignaturePad((err, data)=>{
              if(err) return
              if(data && typeof data === 'string') {
                me.setProperty('value', data, true)
              }
            })
          },
        })
      }
    }else if(type==='files' || type === 'images') {
      _.extend(editor, {
        tag: 'mele-uploader',
        readonly: this.readonly,
        multi: this.multi,
        mime: this.mime,
        rendertype: this.rendertype || PROPERTIES.rendertype?.default,
        eventFilesChanged: async (self, items, old, user_interaction)=>{
          if(!me.readonly && !me.disabled && user_interaction) {
            if(items.length > 0) {
              const result = await self.base64FromBlob(items)
              me.setProperty('value', result, true)
            } else {
              me.setProperty('value', null, true)
            }
          }
        },
      })
    }else if(type==='image') {
      _.extend(editor, {
        tag: 'img',
        onclick: (e)=>{
          if(!this.value) return
          const elMod = this.modalOpen({
            key: ':input',
            position: 'center',
            outclick: ':modal.cancel',
            inclick: ':modal.cancel',
            class: 'modal-preview',
          })

          elMod._body.appendChildren({
            tag: 'img',
            src: e.target.src,
            class: 'preview',
            alt: ' ',
          })
        },
      })

      const eLImg = this
      const elImageName = _.uniqueId('image_')
      if(!this._imageUploadField) {
        this._elValueWrapper.classList.add(this.classScope('content'))
        this._imageUploadField = this._elValueWrapper.appendChildren({
          tag: 'input',
          type: 'file',
          inputmode: this.inputmode || 'image',
          id: elImageName,
          class: 'input-upload',
          accept: 'image/*',
          onchange: (el)=>{
            const reader = new FileReader()
            reader.onload = ()=> {
              eLImg.value = reader.result
              this._imageUploadLabel.hidden = true
              this._elClear.hidden = false
            }
            reader.onerror = (e)=>{
              console.error(e)
            }
            const file = el.target.files[0]
            if(file) {
              reader.readAsDataURL(file)
            }
          },
        })
        this._imageUploadLabel = this._elValueContent.appendChildren({
          tag: 'label',
          for: elImageName,
          class: 'upload-button',
          html: this.translate('image.upload'),
        })
      }
      this._elClear = this._elValueContent.appendChildren({
        tag: 'mele-button',
        key: 'close',
        type: 'iconmini',
        class: 'image-clear',
        iconleft: 'u.clear',
        hidden: true,
        eventButtonClicked: ()=>{},
        onclick: ()=>{
          this.value = ''
          this._imageUploadLabel.hidden = false
          this._elClear.hidden = true
          this._imageUploadField.value = ''
        },
      })
      if(this.readonly) {
        this._imageUploadLabel.hidden = true
        this._elClear.hidden = true
      } else {
        if(this.value === '') {
          this._imageUploadLabel.hidden = false
        } else {
          this._elClear.hidden = false
        }
      }
    }else if(type==='file') {
      if(!this._filesWrapper) {
        this._elValueContent.classList.add(this.classScope('file'))
        this._filesWrapper = this._elValueContent.appendChildren({
          tag: 'ul',
          class: 'files-wrapper',
          onclick: (e)=> {
            if(!this.readonly && e.target === this._filesWrapper) { this._elValueElement.click() }
          }, // triggers input box
        })
        this._filesWrapper.setAttribute('label', this.translate('file.upload'))
      }
      _.extend(editor, {
        tag: 'input',
        inputmode: this.inputmode || 'file',
        type: 'file',
        onchange: ()=>{
          const files = this._elValueElement.files
          const filesLength = files.length
          let i = 0
          const values = []

          while (i < filesLength) {
            const file = files[i]
            const reader = new FileReader()
            reader.onload = (data)=>{
              const value = data.target.result.split(';')
              value.splice(1, 0, `name=${file.name.trim().replace(/\s/g, '_')}`)
              values.push(value.join(';'))
              if(values.length !== filesLength) return
              me.setProperty('value', values.join('∫'), true)
            }
            reader.readAsDataURL(file)
            i++
          }
          // clear value element
          this._elValueElement.value=''
        },
      })
    }else if(type==='fields') {
      _.extend(editor, {
        tag: 'mele-view',
      })
    }else if(type==='geoshape') {
      _.extend(editor, {
        tag: 'mele-map',
        maxzoom: _.get(customElements.get('mele-map'), '_properties.maxzoom.default', 10),
        zoom: _.get(customElements.get('mele-map'), '_properties.zoom.default', 14),
        fitzoom: _.get(customElements.get('mele-map'), '_properties.fitzoom.default', 14),
        layers: _.toString(this.itemicon),
        searchbar: false,
      })

      this._elValueContent.classList.add(this.classScope('text'))
      this._elLegend = this._elValueContent.appendChildren({
        tag: 'mele-text',
        key: 'geolegend',
      })

      if(this.readonly) {
        editor.disabled = true
        this._elValueContent.forAll('@geocurrent,@geochange', el=>el.deleteElement())
      }else{
        this._elValueContent.appendChildren([
          {
            tag: 'mele-button',
            key: 'geochange',
            type: 'toolbar',
            color: 'input',
            iconleft: 'u.edit',
            eventButtonClicked: async()=>{ await this.editGeo() },
          }])
      }
    }else if(type==='rotary') {
      _.extend(editor, { tag: 'span' })
      if(!this.readonly) {
        this._elValue.onmousedown = this.startRotary.bind(this)
        this._elValue.ontouchstart = this.startRotary.bind(this)
      }
    }else if(type==='checkmark') {
      let iconValue
      try {
        iconValue = this.value ? parseInt(this.value) : 0
      } catch (error) {
        iconValue = 0
      }
      _.extend(editor, {
        tag: 'mele-image',
        icon: `checkmark.${iconValue}`,
      })
    }else if(this.readonly) {
      if(/^(dropdown|lookup|combo|list|radio|group|grid|ovals|tags|slider|users)$/.test(type)) {
        _.extend(editor, {
          tag: 'mele-list',
          class: 'list-view',
          direction: /^(dropdown|lookup|combo|list|radio)$/.test(type) ? this.multi ? 'row' : 'column' : 'row',
          show: /^(dropdown|lookup|combo|users)$/.test(type) ? 'selected' : 'all',
          multi: this.multi,
          auto: this.auto,
          blank: this.blank,
          design: /^(dropdown)/.test(type) && this.multi ? 'oval' : 'none',
          // prefix: this.prefix,
          label: _.toString(this.itemlabel),
          label2: _.toString(this.itemlabel2),
          value: _.toString(this.itemvalue),
          color: _.toString(this.itemcolor),
          iconright: _.toString(this.itemicon),
          sort: this.sort,
          prompt: this.label,
          readonly: true,
        })
        if(/^(lookup|combo|users)$/.test(type)) {
          editor.searchbar = true
        }
        if(/^(combo)$/.test(type)) {
          editor.editable = true
        }
      }else if(type==='richtext') {
        _.extend(editor, { tag: 'div' })
      }else if(type==='checkmark') {
        // do nothing
      }else if(type==='keyvalue') {
        _.extend(editor, { tag: 'div' })
      } else {
        _.extend(editor, { tag: 'span' })
      }
    }else if(type==='text' || type==='keyvalue') {
      _.extend(editor, {
        tag: 'textarea',
        class: 'editfield',
        oninput: function() {
          const selectionposition = this.selectionStart
          me.setProperty('value', this.value, true)
          this.selectionStart = selectionposition
          this.selectionEnd = selectionposition
        },
        onfocus: (e)=>{
          if(this.focusFromMethod) {
            // reset vars
            this.focusFromMethod = false
            // internal focus() will hapen, no need to go into our method
          } else {
            // internal focus() happend, but we still need to send messages
            this.inputFocus(false, true)
          }
        },
        onblur: ()=>{
          // internal blur just happend

          if(this.fromKeyboard) {
            // reset vars
            this.fromKeyboard = false
            // event came from keyboard, so we need to refocus, no need for messages
            this.focusFromMethod = true
            this._elValueElement.focus()
          }else {
            this.focusedChanged(false)
          }
        },
        onkeypress: handleKeyPress.bind(me),
      })
    }else if(type==='richtext') {
      _.extend(editor, {
        tag: 'mele-editor',
        placeholder: this.placeholder,
        toolbar: this.toolbar,
        eventHtmlChanged: (el, html, deltas)=>{
          if(html === '<p><br></p>') {
            html = ''
          }
          me.setProperty('value', html, true)
        },
      })
    }else if(type==='check') {
      _.extend(editor, {
        tag: 'mele-button',
        type: 'icon',
        // eslint-disable-next-line no-template-curly-in-string
        iconleft: '${prefix}${selected}',
        sort: this.sort,
        eventButtonClicked: (self)=>{
          if(!me.readonly && !me.disabled) {
            self.selected = !self.selected
            me.setProperty('value', self.selected?1:0, true)
          }
        },
      })
    }else if(type==='yesno') {
      _.extend(editor, {
        tag: 'mele-list',
        class: 'list-view',
        direction: 'row',
        show: 'all',
        // eslint-disable-next-line no-template-curly-in-string
        label: '${prefix}${key}',
        items: [0, 1],
        selected: [],
        eventIconClicked: (el, item)=>{ el.itemClicked(item) },
        eventSelected: (self, items, old, user_interaction)=>{
          if(!me.readonly && !me.disabled && user_interaction) { me.setProperty('value', items.join('∫'), true) }
        },
      })
    }else if(type==='icongroup' || type==='iconrow') {
      _.extend(editor, {
        tag: 'mele-list',
        class: 'list-view',
        direction: 'row',
        show: 'all',
        multi: this.multi,
        auto: this.auto,
        // eslint-disable-next-line no-template-curly-in-string
        iconleft: '${prefix}${key}',
        sort: this.sort,
        eventIconClicked: (el, item)=>{ el.itemClicked(item) },
        eventSelected: (self, items, old, user_interaction)=>{
          if(!me.readonly && !me.disabled && user_interaction) { me.setProperty('value', items.join('∫'), true) }
        },
      })
    }else if(/^(slist|slookup|sdropdown)$/.test(type)) {
      _.extend(editor, {
        tag: 'mele-dropdown',
        class: 'editfield dropdown-wrapper',
        prefix: this.prefix,
        sort: this.sort,
        prompt: this.label,
        label: _.toString(this.itemlabel),
        label2: _.toString(this.itemlabel2),
        value: _.toString(this.itemvalue),
        color: _.toString(this.itemcolor),
        tabindex: this.tabindex || 0,
        items: this.options,
        eventSelected: (self, items, old, user_interaction)=>{
          if(!me.readonly && !me.disabled && user_interaction) {
            me.setProperty('value', items.join('∫'), true)
          }
        },
      })
      this._elClear = this._elValueContent.appendChildren({
        tag: 'mele-button',
        type: 'iconmini',
        key: 'clear',
        class: 'double-icons',
        iconleft: 'u.clear',
        eventButtonClicked: ()=>{},
        onclick: ()=>{
          this.value = ''
        },
      })
      if(/^(slookup)$/.test(type)) { editor.searchbar = true }
    }else if(/^(lookup|dropdown|combo|list|radio|group|grid|ovals|tags|slider|users)$/.test(type)) {
      _.extend(editor, {
        tag: 'mele-list',
        class: 'editfield list-view',
        direction: /^(dropdown|lookup|combo|list|radio)$/.test(type) ? 'column' : 'row',
        show: /^(dropdown|lookup|combo|users)$/.test(type) ? 'selected' : 'all',
        multi: this.multi,
        auto: this.auto,
        blank: this.blank,
        readonly: false,
        // prefix: this.prefix,
        label: _.toString(this.itemlabel),
        label2: _.toString(this.itemlabel2),
        value: _.toString(this.itemvalue),
        color: _.toString(this.itemcolor),
        iconright: _.toString(this.itemicon),
        sort: this.sort,
        prompt: this.label,
        eventIconClicked: (el, item)=>{ el.itemClicked(item) },
        eventSelected: (self, items, old, user_interaction)=>{
          if(!me.readonly && !me.disabled && user_interaction) { me.setProperty('value', items.join('∫'), true) }
          if(type === 'users' && me._elValueElement) {
            me._elValueElement.modalClose(':dropdown')
          }
        },
      })
      if(editor.direction==='row') editor.horizontal = true
      if(/^(lookup|combo|users)$/.test(type)) {
        editor.searchbar = true
      }
      if(/^(combo)$/.test(type)) {
        editor.editable = true
      }
    }else if(/duration/.test(type)) {
      this.format = this.format || type
      if(!this.readonly) {
        _.extend(editor, {
          tag: 'input',
          class: 'editfield',
          inputmode: this.inputmode,
          size: 1,
          type: 'text',
          oninput: (e)=>{
            me._dateSelectionPosition = e.target.selectionStart
            const valid = this.validateFormat()
            if(valid) {
              const val = moment.duration(e.target.value, TIMEFORMAT[me.format]).valueOf()
              me.setProperty('value', val, true)
            } else {
              me.setProperty('value', e.target.value, 'string')
            }
          },
          onkeypress: handleKeyPress.bind(me),
        })
        // this._elClear = this._elValueContent.appendChildren({
        //   tag: 'mele-button',
        //   type: 'iconmini',
        //   key: 'clear',
        //   class: 'double-icons',
        //   iconleft: 'u.clear',
        //   eventButtonClicked: ()=>{},
        //   onclick: ()=>{ me.setProperty('value', '', true); if(this.format != 'extended') this.find('.editfield').focus() },
        // })
        // this._elEdit = this._elValueContent.appendChildren({ tag: 'mele-button', type: 'iconmini', key: 'edit', iconleft: 'u.calendar', eventButtonClicked: this.editDatetime.bind(this) })
      } else {
        _.extend(editor, {
          tag: 'span',
        })
      }
    }else if(/date|time/.test(type)) {
      this.format = this.format || type
      if(!this.readonly) {
        _.extend(editor, {
          tag: 'input',
          class: 'editfield',
          inputmode: this.inputmode,
          size: 1,
          type: 'text',
          oninput: (e)=>{
            me._dateSelectionPosition = e.target.selectionStart
            const valid = this.validateFormat()
            if(valid && e.target.value) {
              let val = moment(e.target.value, TIMEFORMAT[me.format] || 'DD.MM.YYYY HH:mm:ss').unix()
              val = moment(val).isValid() ? /ms$/.test(type) ? val*1000 : val : ''
              if(me.format==='datetime_us' || me.format==='datetimeus') {
                const m = /(\.\d+)$/.exec(e.target.value)
                if(m) val = parseFloat(val+m[1])
                val *= 1000000
              }
              me.setProperty('value', val, true)
            } else {
              me.setProperty('value', e.target.value, 'string')
            }
          },
          onkeypress: handleKeyPress.bind(me),
        })
        this._elClear = this._elValueContent.appendChildren({
          tag: 'mele-button',
          type: 'iconmini',
          key: 'clear',
          class: 'double-icons',
          iconleft: 'u.clear',
          eventButtonClicked: ()=>{},
          onclick: ()=>{ me.setProperty('value', '', true); if(this.format !== 'extended') this.find('.editfield').focus() },
        })
        this._elEdit = this._elValueContent.appendChildren({
          tag: 'mele-button',
          type: 'iconmini',
          key: 'edit',
          iconleft: 'u.calendar',
          eventButtonClicked: this.editDatetime.bind(this),
        })
      } else {
        _.extend(editor, {
          tag: 'span',
        })
      }
    }else if(type==='string' || type==='password' || type==='rotarystring') {
      _.extend(editor, {
        tag: 'input',
        type: type==='password'?'password':'text',
        inputmode: this.inputmode,
        class: 'editfield',
        size: 1,
        oninput: function() {
          // Save the initial cursor position
          const selectionPosition = this.selectionStart

          // Save the length of the value before transformation
          const originalLength = this.value.length

          // Transform the value
          const transformedValue = me.doTransformation(this.value)

          // Set the new transformed value
          this.value = transformedValue

          // Calculate the length difference
          const lengthDifference = this.value.length - originalLength

          // Adjust the cursor position based on the transformation
          const newPosition = selectionPosition + lengthDifference

          // Set the new cursor position
          this.selectionStart = newPosition
          this.selectionEnd = newPosition

          // Update the internal property
          me.setProperty('value', this.value, true)
        },
        onfocus: (e)=>{
          if(this.focusFromMethod) {
            // reset vars
            this.focusFromMethod = false
            // internal focus() will hapen, no need to go into our method
          } else {
            // internal focus() happend, but we still need to send messages
            this.inputFocus(false, true)
          }
        },
        onblur: ()=>{
          // internal blur just happend

          if(this.fromKeyboard) {
            // reset vars
            this.fromKeyboard = false
            // event came from keyboard, so we need to refocus, no need for messages
            this.focusFromMethod = true
            this._elValueElement.focus()
          } else {
            this.focusedChanged(false)
          }
        },
        onkeypress: handleKeyPress.bind(me),
      })

      if(type!=='rotarystring') {
        this._elValueContent.appendChild(clear_icon_template.cloneNode(true))
        this._elClear = this._elValueContent.children[this._elValueContent.children.length-1]
        this._elClear.onclick = ()=>{
          this._elValueElement.value = ''
          this.setProperty('value', '', true)
          if(this.format !== 'extended') this.find('.editfield').focus()
        }
      }
      if(!this.readonly && type==='rotarystring') {
        this._elEdit = this._elValueContent.appendChildren({ tag: 'mele-button', type: 'iconmini', key: 'edit', iconleft: 'rotary' })
        this._elEdit.onmousedown = this.startRotary.bind(this)
        this._elEdit.ontouchstart = this.startRotary.bind(this)
        this._elEdit.onpointerdown = this.startRotary.bind(this)
        this._elEdit.contextmenu = (e)=>e.preventDefault()
      }
    }else if(type==='invisible') {
      _.extend(editor, {
        tag: 'input',
        inputmode: 'none',
        type: 'text',
        class: 'editfield',
      })
    }else{
      return
    }

    // update editing element if the tag remains the same, otherwise replace it
    if(_.toLower(this._elValueElement.tagName)===editor.tag) {
      this._elValueElement.setProperties(editor, true)
    } else {
      this._elValueElement = this._elValueElement.replaceElementFromJS(editor)
    }

    if(this.readonly && this.value && this.type === 'richtext') {
      this._elValueElement.innerHTML = this.value
    }

    if(type==='geoshape') {
      this._elValueElement.setItem({
        key: 'selected',
        marker: _.toString(this.itemicon),
        label: _.toString(this.itemlabel),
        color: _.toString(this.itemcolor),
      })
      this.onclick = ()=>{
        if(this.readonly) this.viewGeo()
      }
    }

    if(type==='graph' && !this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(this.resized.bind(this)).observe(this)
    }

    // set up editing element status
    this.rowsChanged()
    this.designChanged()
    this.multiChanged()
    this.prefixChanged()
    this.autoChanged()
    this.optionsChanged()
    this.placeholderChanged()
    this.valueChanged(this.value, this.value)
    this.validate()
  } // typeChanged

  focusedChanged(value) {
    if(value && this.type === 'text' && (this.closest('.mele-window') && this.closest('.mele-window').behavior && this.closest('.mele-window').behavior === 'phone')) {
      setTimeout(()=>{
        this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 400)
    } else if(!value) {
      this.removeAttribute('focused')
    }
  }

  roundingChanged(value) {
  }

  tabindexChanged(value) {
    if(!isNaN(value)) {
      this._elValueElement.tabindex = value
    }
  }

  dataURItoBlob(dataURI) {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    const bb = new Blob([ab], { type: mimeString })
    return bb
  }

  renderFileIntoList(base64, readonly) {
    if(!base64) return
    const [type, name, value] = base64.split(';')
    const m = name.match(/(?:name=)(.+)/)
    const label = m ? m[1] : ''
    const me = this
    const file = {
      tag: 'li',
      class: 'input-file',
      children: [
        { tag: 'a', class: 'file', html: `${label}` },
        {
          tag: 'span',
          class: 'remove-file',
          html: '<strong>x</strong>',
          onclick: ()=>{
            const values = me.value.split('∫')
            const remove = values.indexOf(base64)
            if(remove > -1) {
              values.splice(remove, 1)
              me.setProperty('value', values.join('∫'), true)
            }
            URL.revokeObjectURL(base64)
            const elFile = this.closest('.input-file')
            if(elFile) elFile.deleteElement()
          },
        },
      ],
    }

    if(readonly) {
      _.extend(file.children[0], {
        href: URL.createObjectURL(this.dataURItoBlob(base64)),
        download: label,
      })
      _.extend(file.children[1], { hidden: true })
    }

    this._filesWrapper.appendChildren(file)
  }

  heightChanged(value) {
    if(value) {
      this._elValueElement.height = value
    } else {
      this._elValueElement.height = null
    }
  }

  inputmodeChanged(value) {
    if(this._elValueElement.tagName === 'INPUT') {
      if(value) {
        this._elValueElement.setAttribute('inputmode', value)
      } else {
        this._elValueElement.removeAttribute('inputmode')
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @desc
  * call focus on an input and send messages realted to focusing
  * @param {boolean} fromKeyboard - flag to mark if the inputFocus was called from the keyboard
  * @param {boolean} fromFocusEvent - flag to mark if the inputFocus was called from the js focus() a.k.a someone clicked in
  */
  inputFocus(fromKeyboard, fromFocusEvent) {
    fromFocusEvent = !!fromFocusEvent
    this.fromKeyboard = fromKeyboard = !!fromKeyboard

    // if event is form keyboard, or it didn't come from js focus(), then it's a
    // method envoked focus and we need to call normal focus(), also we set a flag
    // to avoid infitie loop
    if(fromKeyboard || !fromFocusEvent) {
      this.focusFromMethod = true
      this._elValueElement.focus()
    }
    // all focuses should send mmInputFocus messages, except if they are from keyboard
    if(!fromKeyboard) {
      this.setProperty('focused', true, true)

      const holder = this.closest('.mele-container:not([propagate])')
      if(!holder || !holder.key) return
      this.publish('mmInputFocused', { key: this.key, holder: holder.key })
    }
  } // inputFocus

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "hide"
  */
  showChanged(value) {
    if(!Array.isArray(value) || value.length !== 2) return
    const [key, val] = value

    const holder = this.closest('[view]')
    const el = holder.find('@{'+key+'}')
    if(el) {
      if(!this._elObserver) {
        if(_.isBoolean(val)) {
          this.hidden = !!el.value
        } else {
          this.hidden = el.value !== val
        }
      }
      this._elObserver = new MutationObserver(mutation=>{
        if(_.isBoolean(val)) {
          this.hidden = !!el.value
        } else {
          this.hidden = el.value !== val
        }
      })
      this._elObserver.observe(el, { attrubutes: true, attributeFilter: ['value'], childList: false, characterData: false })
    }
  } // hideChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @desc
  * call blur on an input and send messages realted to focusing
  */
  inputBlur() {
    // do the blur itself
    this._elValueElement.blur()
    // disable the property focused
    this.removeAttribute('focused', false, true)
    this.fromKeyboard = false
    this.focusFromMethod = false

    const holder = this.closest('.mele-container:not([propagate])').key
    if(holder) { this.publish('mmInputBlured', { key: this.key, holder }) }
  } // inputFocus

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "maxlength"
  */

  maxlengthChanged(value) {
    if(value) {
      //
    }
  }

  searchbarChanged(value) {
    if(this.type !== 'geoshape') return
    this._elValueElement.searchbar = value
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "steps"
  */
  stepsChanged() {
    const el = this._elValueContent
    const steps = this.steps
    const me = this
    if(steps) {
      steps.forEach(step=>{
        el.appendChildren({
          tag: 'mele-button',
          type: 'action',
          label: '+'+step,
          key: 'asc',
          class: 'button-fluid',
          eventButtonClicked: ()=>{
            if(me.disabled) return
            const old_val = this.value
            const new_val = parseInt(old_val) + parseInt(step)
            this.setProperty('value', new_val, true)
            this.valueChanged(this.value, old_val, true)
          },
        })
      })
      steps.forEach(step=>{
        el.prependChildren({
          tag: 'mele-button',
          type: 'action',
          key: 'desc',
          label: '-'+step,
          class: 'button-fluid',
          eventButtonClicked: ()=>{
            if(me.disabled) return
            const old_val = this.value
            const new_val = parseInt(old_val) - parseInt(step)
            this.setProperty('value', new_val, true)
            this.valueChanged(this.value, old_val, true)
          },
        })
      })
    }
  } // rowsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "rows"
  */
  rowsChanged() {
    const el = this._elValueElement
    if(_.toLower(el.tagName)!=='textarea') return
    el.rows = this.rows
  } // rowsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "design"
  */
  designChanged() {
    const el = this._elValueElement
    const tagname = _.toLower(el.tagName)
    if(tagname==='mele-map') {
      el.base = this.design
    }else if(tagname==='mele-list' || tagname==='mele-dropdown') {
      const DEFAULT_TYPE_DESIGNS = {
        yesno: 'group',
        group: 'group',
        icongroup: 'group',
        grid: 'grid',
        ovals: 'oval',
        tags: 'tag',
        slider: 'slider',
        users: 'user',
      }
      el.design = this.design || DEFAULT_TYPE_DESIGNS[this.type] || 'none'
    }
  } // designChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "multi"
  */
  multiChanged() {
    const el = this._elValueElement
    const input_tag = _.toLower(el.tagName)
    if(input_tag==='input' && _.toLower(el.type)==='file') {
      el.multiple = this.multi
    }else if(input_tag==='mele-list') {
      el.multi = this.type==='check' || this.type==='yesno' ? false : this.multi
      if(this.type==='radio') {
        // eslint-disable-next-line no-template-curly-in-string
        el.iconleft = this.multi ? 'sys.check.${selected}' : 'sys.radio.${selected}'
      }
    }
  } // multiChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "disabled"
  */
  disabledChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/-/.test(tag)) {
      el.disabled = this.disabled
    }else{
      el.disabled = this.disabled
      // if(this.type=='richtext' && !this.readonly && !this.disabled){
      //   el.setAttribute('contenteditable', true)
      // }else {
      //   el.removeAttribute('contenteditable')
      // }
    }
    if(this._elClear) {
      if(this.disabled) {
        this._elClear.hidden = true
      } else {
        this._elClear.hidden = false
      }
    }
  } // disabledChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "readonly"
  */
  readonlyChanged() {
    this.typeChanged(this.type, this.type)
  } // readonlyChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "localize"
  */
  localizeChanged() {
    this.valueChanged(this.value, this.value)
  } // localizeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "auto"
  */
  autoChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/date|time/.test(this.type) && this.auto && this.value==='') {
      this.value = parseInt(Date.now() / (/ms/.test(this.type) ? 1 : 1000))
      return
    }
    if(tag!=='mele-list') return
    el.auto = this.auto
  } // autoChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label"
  */
  labelChanged(value, old) {
    super.labelChanged(value, old)
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) {
      el.prompt = value
    }
  } // labelChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "options"
  */
  optionsChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) {
      if(this.type==='check' || this.type==='yesno') return // boolean types have fixed [0,1] options
      if(!this.auto) { el.items = this.options }
    }
  } // optionsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "blank"
  */
  blankChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) {
      el.blank = this.blank
      const selected = el.selected
      if(this.blank && selected.length===0) {
        this.selected = ['']
      }else if(!this.blank && selected.length===1 && selected[0]==='') {
        this.selected = []
      }
    }
    this.validate()
  } // blankChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "itemlabel"
  */
  itemlabelChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) {
      el.label = this.itemlabel
    }else if(/mele-map/.test(tag)) {
      const elItem = el.getItem('selected') || { key: 'selected' }
      elItem.label = this.itemlabel
      el.setItem(elItem)
    }
  } // itemlabelChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "itemlabel2"
  */
  itemlabel2Changed() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) { el.label2 = this.itemlabel2 }
  } // itemlabel2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "itemvalue"
  */
  itemvalueChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) { el.value = this.itemvalue }
  } // itemvalueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "itemcolor"
  */
  itemcolorChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) {
      el.color = this.itemcolor
    }else if(/mele-map/.test(tag)) {
      const elItem = el.getItem('selected') || { key: 'selected' }
      elItem.color = this.itemcolor
      el.setItem(elItem)
    }
  } // itemcolorChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "itemicon"
  */
  itemiconChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(tag==='mele-list') {
      el.iconright = this.itemicon
    }else if(/mele-map/.test(tag)) {
      el.layers = this.itemicon
      const elItem = el.getItem('selected') || { key: 'selected' }
      elItem.marker = this.itemicon
      el.setItem(elItem)
    }
  } // itemiconChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "sort"
  */
  sortChanged() {
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)
    if(/mele-list|mele-dropdown/.test(tag)) { el.sort = this.sort }
  } // sortChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "format"
  */
  formatChanged() {
    if(this.type==='custom') this.valueChanged()
    this.validate()
  } // formatChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "format"
  */
  prefixChanged(value, old) {
    super.prefixChanged(value, old)
    const el = this._elValueElement
    const tag = _.toLower(el.tagName)

    if(value && tag==='mele-dropdown') {
      el.prefix = this.interpolateAndSetDependencies(value, false, 'prefix')
      return
    }

    if(value && this.type === 'autocomplete') {
      const options = this.getOptions(value)
      this._options = []
      this._trOptions = []
      options.forEach(opt=>{
        this._options.push(opt)
        this._trOptions.push(this.translate(value + opt))
      })
    }

    const DEFAULT_TYPE_PREFIX={
      check: 'sys.check.',
      yesno: 'yesno.',
    }
    const prefix = this.prefix || DEFAULT_TYPE_PREFIX[this.type]
    if(tag==='mele-list' || tag==='mele-button') {
      el.prefix = prefix
      const new_prefix = this.interpolateAndSetDependencies(prefix, false, 'prefix')
      if(el.prefix !== new_prefix) {
        el.prefix = new_prefix
        if(tag==='mele-list') this.setProperty('value', el.selected.join('∫'), true)
      }
    } else if(tag==='span') {
      // translate readonly content of sdropdown
      if(value !== old) {
        this.valueChanged()
      }
    } else {
      // this.valueChanged()
    }
  } // prefixChanged

  isCanvasBlank(canvas) {
    const context = canvas.getContext('2d')

    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer,
    )

    return !pixelBuffer.some(color => color !== 0)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "value"
  */
  valueChanged(value, old, context_user_interaction) {
    value = _.toString(this.value)

    this.clearDependencies('value')

    if(this.type==='custom') {
      const calc = this.format
      const meleWindow = this.closest('.wf.mele-window')
      const windowModule = meleWindow ? meleWindow.module : null
      const wfScripts = windowModule && windowModule.workflow ? windowModule.workflow.parsed_scripts || {} : {}
      try{
        const fcn =
          (typeof this[calc]==='function') ? this[calc] :
            (typeof wfScripts[calc]==='function') ? wfScripts[calc] :
              (typeof window[calc]==='function') ? window[calc] :
                /^{{.*}}$/.test(calc) ? function(value) { this._elValueElement.innerHTML = this.translateAndSetDependencies(calc, false, 'format') } :
                  // eslint-disable-next-line no-new-func
                  new Function('value', 'old', 'context_user_interaction', calc)
        const WORKFLOW = wfScripts
        fcn.call(this, value, old, context_user_interaction)
      }catch(e) {
      }
      return
    }

    if(this.type === 'attachments') {
      this._elValueElement.files = value.split('∫').filter(f=>f)
      return
    }

    if(this.type === 'keyvalue') {
      value = this.value

      if(this.readonly) {
        try{
          const obj = typeof value === 'string' ? JSON.parse(value) : value
          const pairs = Object.entries(obj)
          const table = document.createElement('table')
          for(const [k, v] of pairs) {
            const row = table.insertRow()
            const cellKey = row.insertCell()
            cellKey.textContent = k
            const cellValue = row.insertCell()
            cellValue.textContent = v
          }
          this._elValueElement.innerHTML = ''
          this._elValueElement.appendChild(table)
        }catch{
          if(value === null || value === undefined || value === '') {
            this._elValueElement.innerHTML = '-'
          }else {
            this._elValueElement.innerHTML = String(value)
          }
        }
      }else {
        try{
          if(typeof value === 'object') value = JSON.stringify(value)
          this._elValueElement.innerHTML = value
        }catch{
          this._elValueElement.innerHTML = value
        }
      }
      return
    }

    if(this.type === 'latlng') {
      const latLng = geo.toLatLng(value)
      if(latLng) {
        this._elValueElement.innerHTML = latLng.map(deg=>(parseFloat(deg.toFixed(5))!==0 ? deg.toFixed(5) : 0)+'°').join(', ')
      }else {
        this._elValueElement.innerHTML = '-'
      }
      return
    }

    // ensure boolean types have always 0/1
    if(this.type==='yesno' || this.type==='check') {
      const value = this.value
      const int_value = value>0 ? '1' : '0'
      if(parseInt(value) !== parseInt(int_value)) {
        this.value = int_value
        return
      }
    }

    // update graph
    if(this.type==='graph') {
      this.drawGraph()
      return
    }

    if(this.type === 'autocomplete') {
      if(!context_user_interaction) {
        const idx = this._options.indexOf(value)
        this._elValueElement.innerHTML = idx > -1 ? this._trOptions[idx] : value
      } else {
        // this._elValueElement.dispatchEvent('input')
      }
    }

    const el = this._elValueElement
    const tag = _.toLower(el.tagName)

    if(this.type==='string' && this.readonly && this.transform.length>0 && !context_user_interaction) {
      value = this.doTransformation(value)
    }

    if(this.type==='checkmark') {
      let iconValue
      try {
        iconValue = this.value ? parseInt(this.value) : 0
      } catch (error) {
        iconValue = 0
      }
      this._elValueElement.icon = iconValue ? 'checkmark.1' : 'checkmark.0'
    }else if(this.type==='icon') {
      el.icon = this.prefix+value
    } else if(this.type==='signature') {
      // if(value && /^data:/.test(value) && this.isCanvasBlank(this._elValueElement.canvas)) {
      //   const ctx = this._elValueElement.canvas.getContext('2d')
      //   const img = new Image()
      //   img.onload = function() { ctx.drawImage(img, 0, 0) }
      //   img.src = value
      // } else {
      //   const canvas = this._elValueElement.canvas
      //   const ctx = canvas.getContext('2d')
      //   ctx.clearRect(0, 0, canvas.width, canvas.height)
      // }
    } else if(this.type==='files' || this.type==='images') {
      if(value && !context_user_interaction) {
        this._elValueElement.blobFromBase64(value)
      } else if(value === '') {
        this._elValueElement.deleteItems()
      }
    } else if(this.type==='image') {
      if(this.clearInput && this.uploadLabel) {
        this.clearInput.deleteElement()
        this.uploadLabel.deleteElement()
      }
      if(value) {
        // check if file is ready to parse
        if (/^[\],:{}\s]*$/.test(value.replace(/\\["\\/bfnrtu]/g, '@')
          .replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g, ']')
          .replace(/(?:^|:|,)(?:\s*\[)+/g, ''))) {
          const obj = JSON.parse(value)
          el.src = obj[Object.keys(obj)[0]]
        }else{
          el.src = value
        }
        this._imageUploadLabel.hidden = true
        this._elClear.hidden = false
      }else{
        el.src = ''
        if(this._imageUploadField) this._imageUploadField.value = ''
        this._imageUploadLabel.hidden = false
        this._elClear.hidden = true
      }
    } else if(this.type==='file') {
      // we should have an array of base64 strings split with '∫' seperator
      this._filesWrapper.innerHTML = ''
      value.split('∫').forEach(file=>{
        this.renderFileIntoList(file, this.readonly)
      })
    } else if(this.type==='fields') {
      let items
      try{
        items = JSON.parse(value)
      }catch(e) {}
      if(this.readonly) {
        items = Array.isArray(items) ? items : [items]
        items.forEach(item=>{
          if(_.isObjectLike(item)) { item.readonly = true }
        })
      }
      el.setItems(items)
    }else if(this.type==='geoshape') {
      value = _.toString(value).match(/^((\w+)(?:\.\w*)?)(?:;([\w\\,]*))?(?:\/(.*))?$/)
      if(value) {
        let [, geoid, center, shape, legend] = value
        if(!legend && center) {
          const addresses = geo.searchGeohash(center, 3, true)
          if(addresses.length>0) {
            const street = _.toString(addresses[0].street)
            const city = _.toString(addresses[0].city)
            legend = street+city ? street+', '+city : street+city
          }
        }
        legend = _.toString(legend).splitCorrect('∫')
        if(shape) {
          const legend_text =
            legend[0] ? _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4])) :
              _.joinStrings(', ', legend[1], legend[4])
          this._elLegend.label = legend_text==='' ? '' : '{{around}} '+legend_text
        }else{
          const legend_text = _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4]))
          this._elLegend.label = legend_text==='' ? '' : '{{}}'+legend_text // to prevent translation of the text
        }
        el.unavailable = false
        el.center = center

        const elItem = el.getItem('selected') || { key: 'selected' }
        el.layers = elItem.marker = this.itemicon || 'marker'
        elItem.shape = shape || center
        el.setItem(elItem)
        el.fitItems()
      }else{
        el.unavailable = true
        el.deleteItems()
        this._elLegend.label = ''
      }

      if(context_user_interaction &&_.isNil(this.initialvalue)) { this.initialvalue = _.toString(old) }
    }else if(/duration/.test(this.type)) {
      this._timeFormat = TIMEFORMAT[this.format] || this.format || TIMEFORMAT[this.type]
      let val
      if(context_user_interaction) {
        val = this.validateFormat() ? val : ''
      } else {
        val = value
      }
      let html = ''
      if(val) {
        const duration = moment.duration(val)
        html = moment.utc(duration.as('milliseconds')).format(this._timeFormat)
      }
      if(this.readonly && el.innerHTML !== html) {
        el.innerHTML = html
      } else if(el.value !== html && !context_user_interaction) {
        this._dateSelectionPosition = html.length
        el.value = html
      }
    }else if (/date|time/.test(this.type)) {
      if(/date|time/.test(this.type) && this.auto && this.value==='' && !context_user_interaction) {
        this.value = parseInt(Date.now() / (/ms/.test(this.type) ? 1 : 1000))
        return
      }
      if(context_user_interaction === 'string') {
        if(this.format !== this.type) {
          const m = this.format.match(/(\w+)\?(\w+)/)
          if(m && m[0]) {
            if(value.length <= TIMEFORMAT[m[1]].length+1) {
              this._dateFormat = TIMEFORMAT[m[1]]
            } else {
              this._dateFormat = TIMEFORMAT[m[1]+m[2]]
            }
          }
        } else {
          delete this._dateFormat
        }
        el.value = value
      } else {
        this._dateFormat = TIMEFORMAT[this.format] || this.format || TIMEFORMAT[this.type]
        const val = /us/.test(this.type) ? parseInt(value)/1000000 : /ms/.test(this.type) ? parseInt(value)/1000 : parseInt(value)
        let html = ''
        if(moment.unix(val).isValid()) {
          html =
            this._dateFormat==='time_us' ? moment.unix(val).format('HH:mm:ss')+'.'+String(val * 1_000_000 % 1_000_000).padStart(6, '0') :
              this._dateFormat==='datetime_us' ? moment.unix(val).format('DD.MM.YYYY HH:mm:ss')+'.'+String(val * 1_000_000 % 1_000_000).padStart(6, '0') :
                moment.unix(val).format(this._dateFormat)
        }
        if(this.readonly && el.innerHTML !== html) {
          el.innerHTML = html
        } else if(el.value !== html) {
          this._dateSelectionPosition = html.length
          el.value = html
        }
      }
      if(this._dateSelectionPosition) {
        el.selectionStart = this._dateSelectionPosition
        el.selectionEnd = this._dateSelectionPosition
        delete this._dateSelectionPosition
      }
      // not sure what this does - check with M.T
      if(context_user_interaction && _.isNil(this.initialvalue)) { this.initialvalue = parseInt(old) }
    }else if (/rotary/.test(this.type)) {
      el.value = value
      el.innerHTML = value
      if(_.isNil(this.initialvalue)) { this.initialvalue = _.toString(old) }
    }else if(context_user_interaction) {
      if(_.isNil(this.initialvalue)) { this.initialvalue = _.toString(old) }
    }else{
      // change input element value only if we were not called by user change on that element
      if(tag==='span') {
        let html
        if(this.type==='check') {
          html = '<mele-image class="check" icon="'+(this.prefix||'sys.check.')+value+'"></mele-image>'
        }else{
          let prefix = _.toString(this.prefix)
          if(prefix==='' && this.type==='yesno') prefix = 'yesno.'
          // escape HTML special characters
          const elPre = this.createElementFast('pre')
          elPre.appendChild(document.createTextNode(value))
          html = elPre.innerHTML

          const defaultEmpty = prefix || (this.type!=='text')
          html = html.split('∫').map(val=>(this.localize || prefix) ? this.translateAndAppendDependencies(prefix+val, defaultEmpty, 'value') : val).join('<sep></sep>')
          if(Array.isArray(this.transform)) {
            const json_indent = this.transform.filter(t=>/^json\d+$/.test(t)).map(t=>parseInt(t.slice(4)))[0]
            if(json_indent>0) {
              try {
                html = JSON.stringify(JSON.parse(html), null, json_indent)
              } catch (e) {
                // ignore
              }
            }
          }
        }
        if(el.innerHTML !== html) el.innerHTML = html
      }else if(tag==='input' || tag==='textarea') {
        if(this.type ==='file'|| this.type === 'image') return
        value = (this.localize || this.prefix) ? this.translateAndAppendDependencies(this.prefix+value, false, 'value') : value
        if(value!==el.value) { el.value = value }
      }else if(tag==='mele-list' || tag==='mele-dropdown') {
        const selected = el.selected
        const current = selected.join('∫')
        if(value==='' && selected.length===0 && this.blank) {
          el.selected = ['']
        }else if(current!==value) {
          el.selected=value===''&&!this.blank?[]:value.split('∫')
        }
      }else if(tag==='mele-button') {
        const selected = value>0
        if(el.selected!==selected) { el.selected=value }
      }else if(this.type==='richtext') {
        if(!this.readonly && !context_user_interaction) {
          this._elValueElement.setHtml(value)
        } else {
          this._elValueElement.innerHTML = value
        }
      } else{
        value = (this.localize || this.prefix) ? this.translateAndAppendDependencies(this.prefix+value, false, 'value') : value
        if(el.innerHTML!==value) { el.innerHTML=value }
      }

      this.initialvalue = null
    }
    if(value!==old && !this.recompute) {
      // valueChanged is called also when language change with value==old

      this.validate()

      const holder = this.closest('.mele-container:not([propagate])')
      if(typeof this.eventValueChanged === 'function') {
        this.eventValueChanged(this, value, old, context_user_interaction)
      }else if(holder && typeof (holder.formValueChanged)==='function') {
        holder.formValueChanged(this, value, old, context_user_interaction)
      }
    }
  } // valueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * when language change, simulate change of all language dependent attributes
  */
  languageChanged() {
    super.languageChanged()
    const tag = _.toLower(this._elValueElement.tagName)
    if(tag==='mele-dropdown') {
      this._elValueElement.languageChanged()
    }
    if(this.type === 'autocomplete') {
      this.prefixChanged(this.prefix, this.prefix)
    }
  } // languageChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "steps"
  */
  transformChanged(transform) {
    if(transform) {
      if(!Array.isArray(transform)) return
      this.transformRules = transform
    }
    this.valueChanged(this.value, this.value)
  } // transformChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Transforms value directly from input into a predefined form, or directly from regexp
  */
  doTransformation(value) {
    if(this.maxlength && (value.length > this.maxlength)) {
      value = value.slice(0, -1)
    }
    if(!this.transformRules) return value

    const cursor_start = this._elValueElement.selectionStart
    this.transformRules.forEach(t=> {
      if(Array.isArray(t)) {
        value = this._transformFromRegexp(t, value)
      } else if(TRANSFORM_TEMPLATES[t]) {
        value = this._transformFromRegexp(TRANSFORM_TEMPLATES[t].split('∫'), value)
      } else if(t==='uppercase') {
        value = value.toUpperCase()
      } else if(t==='lowercase') {
        value = value.toLowerCase()
      } else if(t==='capitalize') {
        value = value.charAt(0).toUpperCase() + value.slice(1)
      } else if(t==='kb') {
        // show as kilobytes
        const num = parseFloat(value)
        if(!isNaN(num)) {
          value = (num / 1024).toFixed(2) + ' KB'
        } else {
          value = '0.00 KB'
        }
      } else if(t==='mb') {
        // show as megabytes
        const num = parseFloat(value)
        if(!isNaN(num)) {
          value = (num / (1024 * 1024)).toFixed(2) + ' MB'
        } else {
          value = '0.00 MB'
        }
      } else {
        value = this._limitCharFromRegexp(t, value)
      }
    })
    return value
  } //doTransformation

  _limitCharFromRegexp(limit, value) {
    try {
      limit = new RegExp(limit)
      const match = value.match(limit)
      if(!match) {
        value = value.slice(0, -1)
      }
      return value
    } catch (e) {
      console.error(e)
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Helper function to transform value form regexp
  */
  _transformFromRegexp(transform, value) {
    // let rule, change, flag, matchrule;
    const [rule, change, flag, matchrule] = transform
    // delete unallowed characters
    if(typeof (matchrule)==='string') {
      value = this._limitCharFromRegexp(matchrule, value)
    }

    // perform formating
    try {
      const reg = new RegExp(rule, flag)
      return value.replace(reg, change)
    } catch (error) {
      console.error(error)
    }
  } //_transformFromRegexp

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "initialvalue"
  */
  initialvalueChanged() {
    this.validate()
  } // initialvalueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "servervalue"
  */
  servervalueChanged() {
    if(_.isNil(this.initialvalue) && !_.isNil(this.servervalue)) { this.value = this.servervalue }
    this.validate()
  } // servervalueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "conflict"
  */
  conflictChanged() {
  } // conflictChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "placeholder"
  */
  placeholderChanged() {
    const el = this._elValueElement
    const html = this.translateAndSetDependencies(this.placeholder, false, 'placeholder')
    if(_.toLower(el.tagName)==='input') {
      el.placeholder = html
    }
  } // placeholderChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "placeholder"
  */
  helpChanged() {
    const html = this.translateAndSetDependencies(this.help, false, 'help')
    if(html==='') {
      this._elValue.forAll('>mele-value-help', el=>el.deleteElement())
    }else{
      const el = this._elValue.find('>mele-value-help') || this._elValue.appendChildren({ tag: 'mele-value-help' })
      if(el.innerHTML !== html) el.innerHTML = html
    }
  } // helpChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "before"
  */
  beforeChanged() {
    const html = this.translateAndSetDependencies(this.before, false, 'before')
    if(html==='') {
      this._elValueWrapper.forAll('>mele-value-before', el=>el.deleteElement())
    }else{
      const el = this._elValueWrapper.find('>mele-value-before') || this._elValueWrapper.prependChildren({ tag: 'mele-value-before' })
      if(el.innerHTML !== html) el.innerHTML = html
    }
  } // beforeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "after"
  */
  afterChanged() {
    const html = this.translateAndSetDependencies(this.after, false, 'after')
    if(html==='') {
      this._elValueWrapper.forAll('>mele-value-after', el=>el.deleteElement())
    }else{
      const el = this._elValueWrapper.find('>mele-value-after') || this._elValueWrapper.appendChildren({ tag: 'mele-value-after' })
      if(el.innerHTML !== html) el.innerHTML = html
    }
  } // afterChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "formaterror"
  */
  formaterrorChanged() {
    const html = this.translateAndSetDependencies(this.formaterror, false, 'formaterror')
    if(html==='' || !this.invalid) {
      this._elValue.forAll('>mele-value-error', el=>el.deleteElement())
    }else{
      const el = this._elValue.find('>mele-value-error') || this._elValue.appendChildren({ tag: 'mele-value-error' })
      if(el.innerHTML !== html) el.innerHTML = html
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Preform validation check
  */
  validate(dont_notify) {
    const value = this.value
    let conflict = false
    let changed = false
    let invalid = false
    let notify = false

    if(this.type==='custom') {
      notify = this.conflict || this.changed || this.invalid
      this.conflict = this.changed = this.invalid = false
    }else{
      // verify if our value has conflict
      const servervalue = this.servervalue
      const initialvalue = this.initialvalue
      conflict = !_.isNil(servervalue) && _.toString(servervalue)!==_.toString(value) && _.toString(servervalue)!==_.toString(initialvalue)
      if(this.conflict !== conflict) {
        this.conflict = conflict
        notify = true
      }

      const html = conflict ? this.translate('Value changed on server to')+': '+servervalue : ''
      if(html==='') {
        this._elValue.forAll('>mele-value-conflict', el=>el.deleteElement())
      }else{
        const el = this._elValue.find('>mele-value-conflict') || this._elValue.appendChildren({ tag: 'mele-value-conflict' })
        if(el.innerHTML !== html) el.innerHTML = html
      }

      // verify if our value has been changed by the user
      changed = conflict || (typeof (initialvalue)==='string' && initialvalue!==value)
      if(this.changed !== changed) {
        this.changed = changed
        notify = true
      }

      // verify if our value is valid
      invalid = !this.validateFormat()
      if(this.invalid !== invalid) {
        this.invalid = invalid
        notify = true
        this.formaterrorChanged()
      }
    }

    if(notify && !dont_notify) {
      const holder = this.closest('.mele-container:not([propagate])')
      if(holder) holder.formStatusChanged(this)
    }

    if(this.width && this.dynamicwidth) {
      this.dynamicwidthChanged(this.dynamicwidth, this.dynamicwidth)
    }
  } // validate

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Preform format validation check
  */
  validateFormat() {
    if(this.type==='custom') return true
    const value = /date|time|duration/.test(this.type) ? (this.readonly ? this._elValueElement.innerHTML : this._elValueElement.value) : this.value

    const min = parseFloat(this.min)
    const max = parseFloat(this.max)

    let format = this.format
    const blank = !!this.blank
    let valid = true
    if(format==='none') {
      return true
    } else if(value==='' && blank) {
      return true
      // blank value OK
    }else if(format==='json') {
      // check "json" format by parsing it
      try { JSON.parse(value) }catch(e) { valid=false }
    }else if(format==='javascript') {
      // check "json" format by parsing it
      try {
        // eslint-disable-next-line no-new-func
        Function(value)
      }catch(e) {
        valid=false
      }
    }else{
      format = VALIDATION_TEMPLATES[format] || format || ((!blank && /dropdown|lookup|combo|list|radio|group|grid|ovals|tags|slider|users|date|time|datetime|geohash/.test(this.type)) ? '.+' : '.*')
      const re = new RegExp(format, 'iu')
      valid = re.test(value)

      if(!isNaN(min) && parseFloat(value)<min) {
        valid = false
      }
      if(!isNaN(max) && parseFloat(value)>max) {
        valid = false
      }
    }
    return valid
  } // validateFormat

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * revert value to servervalue resp. originalvalue
  */
  undo() {
    if(!_.isNil(this.servervalue)) {
      this.value = this.servervalue
    }else if(!_.isNil(this.initialvalue)) {
      this.value = this.initialvalue
    }
    this.servervalue = null
    this.initialvalue = null
  } // undo

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens dialog window for editation of geoshape
  */
  async editGeo() {
    const elModal = this.modalOpen({
      key: ':input',
      class: 'editgeo',
      outclick: ':modal.cancel',
      position: 'full',
      footerbuttons: [
        'cancel',
        {
          color: 'red',
          label: 'form.clear',
          action: 'clear',
        },
        {
          color: 'green',
          label: 'form.save',
          action: 'save',
        },
      ],
      formCancel: ()=>{ elModal.modalClose() },
      formClear: ()=>{
        elMap.deleteItems()
        this.value = ''
      },
    })

    await elModal._initialized
    elModal.formSave = ()=>{
      elModal.modalClose()
      setTimeout(()=>{
        const selected = elMap.selected
        const item = elMap.getItem(selected[0])
        if(item) {
          const is_place = /^place:\w+(\.\w+)?/.test(item.key)
          let geo = is_place ? item.key.replace(/^place:/, '') : item.center
          if(item.shape && item.shape !== geo.replace(/\.\w+/, '')) { geo += ';'+item.shape }
          if(is_place && _.isObjectLike(item.data)) {
            const address = [
              _.toString(item.data.name),
              _.toString(item.data.street),
              _.toString(item.data.cadno),
              _.toString(item.data.streetno),
              _.toString(item.data.city),
              _.toString(item.data.zip),
            ]
            geo += '/'+address.join('∫')
          }
          this.setProperty('value', geo, true)
        }else{
          this.value = ''
        }
      }, 100)
    }

    const elMap = elModal.appendItems({
      tag: 'mele-map',
      view: true,
      formModalclose: ()=>{ elModal.modalClose() },
      searchbar: true,
      shapebutton: true,
      shapelist: true,
      eventSearch: this.eventSearch,
      eventSelected: ()=>{},
    })
    await elMap._initialized

    const value = _.toString(this.value)
    const geo = value.match(/^((\w+)(?:\.\w*)?)(?:;([\w,]*))?(?:\/(.*))?$/)
    let geoid, center, shape, legend
    if(geo) {
      [, geoid, center, shape, legend] = geo
      legend = _.toString(legend).splitCorrect('∫')
      if(shape) {
        const legend_text =
          legend[0] ? _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4])) :
            _.joinStrings(', ', legend[1], legend[4])
        legend = legend_text==='' ? '' : '{{around}} '+legend_text
      }else{
        const legend_text = _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4]))
        legend = legend_text==='' ? '' : '{{}}'+legend_text // to prevent translation of the text
      }
      const map_item = {
        key: 'selected',
        marker: 'marker',
        shape: shape || center,
        label: legend,
      }

      elMap.setItems(map_item)
    }

    elMap.zoom = 14
    elMap.center = center
    elMap.selected = ['selected']
  } // editGeo

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens dialog window for fullscreen view of geoshape
  */
  viewGeo() {
    if(!this._elValueElement || this._elValueElement.unavailable) return

    const elModal = this.modalOpen({
      key: ':input',
      class: 'viewgeo',
      outclick: ':modal.cancel',
      position: 'full',
      headerbuttons: ['modalx'],
      header: this.label,
    })
    const elMap = elModal.appendItems({
      tag: 'mele-map',
      view: true,
      formModalclose: ()=>{ elModal.modalClose() },
    })

    const value = _.toString(this.value)
    const geo = value.match(/^((\w+)(?:\.\w*)?)(?:;([\w,]*))?(?:\/(.*))?$/)
    if(!geo) return

    let [, geoid, center, shape, legend] = geo
    legend = _.toString(legend).splitCorrect('∫')
    if(shape) {
      const legend_text =
        legend[0] ? _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4])) :
          _.joinStrings(', ', legend[1], legend[4])
      legend = legend_text==='' ? '' : '{{around}} '+legend_text
    }else{
      const legend_text = _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4]))
      legend = legend_text==='' ? '' : '{{}}'+legend_text // to prevent translation of the text
    }
    const map_item = {
      key: 'selected',
      marker: 'marker',
      shape: shape || center,
      label: legend,
    }

    elMap.setItems(map_item)
    elMap.zoom = 14
    setTimeout(()=>{
      elMap.center = center
      elMap.selected = ['selected']
    }, 200)
  } // viewGeo

  clearFile() {
    this._elValueContent.find('label').innerHTML = 'Upload file'
    this.value = ''
    this._elValueContent.find('mele-close@close').hidden = true
  }

  dynamicwidthChanged(value) {
    if(!this.value) return

    if(this.width && this.value.length > parseInt(value)) {
      // let's parse current width
      const width = this.width.split('/')
      // we expect to see a
      if(Array.isArray(width) && width.length === 2) {
        const limit = parseInt(width[1])
        if(limit > 1) this.width = '1/' + (limit-1).toString()
      }
    }
  }

  focus() {
    if(this._elValueElement && typeof this._elValueElement.focus === 'function') {
      this._elValueElement.focus()
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens dialog window for editation of datetime
  */
  editDatetime() {
    if(this.cal_mode) {
      this.editDatetime2()
    }else {
      this.editDatetime1()
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens dialog window for editation of datetime
  * uses original Brtka's calendar
  */
  editDatetime1() {
    if(this.disabled || this.readonly) return
    const body = this.ownerDocument.body
    const owner = this.closest('.mele-modal,.mele-window')
    body.forAll('>.mele-modal@{:input}, >.mele-modalwindow@{:input}', el=>el.deleteElement())
    const invoker = this._elValueElement.getBoundingClientRect().width < 250 ? this : this._elValueElement
    const elModal = body.appendChildren({
      tag: 'mele-modal',
      key: ':input',
      class: 'dropdown modal-calendar',
      position: 'over',
      behavior: owner.behavior,
      invoker, // this will set calendar element under the input itself, not the label
      footerbuttons: [
        'cancel',
        { color: 'red', label: 'form.clear', action: 'clear' },
        { color: 'green', label: 'form.save', action: 'save' },
      ],
      formCancel: ()=>{ elModal.modalClose(); document.removeEventListener('keypress', enterPressed) },
      formClear: ()=>{
        elModal.modalClose()
        document.removeEventListener('keypress', enterPressed)
        this.setProperty('value', '', true)
      },
      formSave: ()=>{
        elModal.modalClose()
        document.removeEventListener('keypress', enterPressed)
        let calendarTime = elCalendar.time || moment().unix()
        if(/ms$/.test(this.type)) {
          calendarTime = calendarTime * 1000
        } else if(/us$/.test(this.type)) {
          calendarTime = calendarTime * 1_000_000
        }
        delete this._dateFormat
        this.setProperty('value', calendarTime, true)
      },
    })
    elModal.setAttribute('modal', owner.getAttribute('mod') || owner.getAttribute('modal'))
    document.addEventListener('keypress', enterPressed)

    // this the same as outerclick, but also removes the keypress event
    elModal.onclick = (e)=>{
      if(e.target===elModal) {
        elModal.modalClose()
        document.removeEventListener('keypress', enterPressed)
      }
    }
    // convert seconds since 00:00 to today's date+seconds
    //const time = this.type == 'time' ? moment().startOf('day').add(parseInt(this.value), 'second').unix() : parseInt(this.value)

    const elCalendar = elModal.appendItems({
      tag: 'mele-calendar',
      title: this.prefix ? this.prefix + this.label : this.label,
      time: toUnixTimestamp(this.type, this.value),
      type: this.type,
      tab: this.type==='time' ? 'hour' : this.auto ? 'day' : 'year',
      rounding: this.rounding,
      formModalclose: ()=>{ elModal.modalClose(); document.removeEventListener('keypress', enterPressed) },
    })

    function toUnixTimestamp(type, value) {
      if (/ms$/.test(type)) {
        // Convert from milliseconds to seconds
        return Math.floor(parseInt(value) / 1000)
      } else if (/us$/.test(type)) {
        // Convert from microseconds to seconds
        return Math.floor(parseInt(value) / 1000000)
      } else {
        // Assume value is already in seconds
        return parseInt(value)
      }
    }
    function enterPressed(e) {
      if(e.which === 13) {
        elModal.formSave()
      }
    }
  } // editDatetime1

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens dialog window for editation of datetime
  * this option is using mele-player-calendar
  */
  editDatetime2() {
    if(this.disabled || this.readonly) return
    const body = this.ownerDocument.body
    const owner = this.closest('.mele-modal,.mele-window')
    body.forAll('>.mele-modal@{:input}, >.mele-modalwindow@{:input}', el=>el.deleteElement())
    const invoker = this._elValueElement.getBoundingClientRect().width < 250 ? this : this._elValueElement

    const elModal = body.appendChildren({
      tag: 'mele-modal',
      key: ':input',
      // class: 'dropdown modal-calendar',
      position: 'center',
      behavior: owner.behavior,
      invoker, // this will set calendar element under the input itself, not the label
      outclick: ':modal.cancel',
      items: [
        {
          tag: 'mele-view',
          view: true,
          items: [
            { key: 'time', tag: 'mele-calendar-datetime', label: '', lastunit: this.cal_lastunit },
            { key: 'calendar', tag: 'mele-calendar-player', indexes: this.cal_indexes },
          ],
        },
      ],
      footerbuttons: [
        'cancel',
        { color: 'red', label: 'form.clear', action: 'clear' },
        { color: 'green', label: 'form.save', action: 'save' },
      ],
    })
    elModal.setAttribute('modal', owner.getAttribute('mod') || owner.getAttribute('modal'))

    let sec = /ms$/.test(this.type) ? _.roundBy(parseInt(this.value)/1000, 3) : this.value
    if(!(sec>0)) sec = Math.floor(Date.now()/1000)

    const elTime = elModal.find('@time')
    const elCalendar = elModal.find('@calendar')
    elTime.calendar = elCalendar
    elTime.value = sec
    elTime.selected = this.cal_unit
    elCalendar.source = this.cal_source
    elModal.formCancel = ()=>{
      elModal.modalClose()
    }
    elModal.formClear = ()=>{
      elModal.modalClose()
      this.setProperty('value', '', true)
    }
    elModal.formSave = ()=>{
      const sec = parseInt(elTime.value)
      elModal.modalClose()
      this.setProperty('value', /ms$/.test(this.type) ? sec*1000 : sec, true)
    }
  } // editDatetime

  toText() {
    const type = _.toString(this.type)
    let prefix = _.toString(this.prefix)
    const value = _.toString(this.value)

    if (type === 'datetime' || type === 'date' || type === 'time') {
      return moment(parseInt(value)).isValid() ? moment.unix(parseInt(value)).format(TIMEFORMAT[type]) : ''
    }else if(type === 'datetimems' || type === 'datems') {
      const v = parseInt(value)/1000
      return moment(v).isValid() ? moment.unix(v).format(TIMEFORMAT[type]) : ''
    }else if(type === 'datetimeus' || type === 'dateus') {
      const v = parseInt(value)/1_000_000
      return moment(v).isValid() ? moment.unix(v).format(TIMEFORMAT[type]) : ''
    } else if(type === 'geoshape' || type === 'geohash') {
      return geo.toLatLng(value).join(', ')
    }else if(/^(check|yesno)$/.test(type)) {
      if(prefix==='') prefix = 'yesno.'
      return this.translate(prefix+(value?1:0))
    }else if(/^(dropdown|lookup|combo|list|radio|group|grid|ovals|tags|slider|users|slist|sdropdown|slookup)$/.test(type)) {
      return this.translate(prefix+value)
    }else if(/^(string|text|richtext)$/.test(this.type)) {
      return(
        (prefix!=='' || this.localize) ? this.translate(prefix+value) : value
      )
    }else{
      return ''
    }
  } // toText

  simplify() {
    const repl = this.ownerDocument.createElement('div')
    const cls = this.class.split(' ').filter(i=>!/mele-/.test(i))
    if(this.hidden) {
      cls.push('hidden')
    } else {
      cls.push('mele-input')
    }
    repl.className = cls.join(' ')
    repl.innerHTML = `<div class="label">${this._label ? this._label.innerText : ''}</div><div class="value">${this.toText()}</div>`
    this.parentNode.replaceChild(repl, this)
    return repl
  }

  resized() {
    if(this.type==='graph') this.drawGraph()
  }

  drawGraph() {
    if(!(this.clientWidth>0 && this.clientHeight>0)) return
    this._elValueElement.setAttribute('width', this.clientWidth-20)
    this._elValueElement.setAttribute('height', this.clientHeight-20)
    this._elValueElement.style = ''
    let json
    try{
      json = JSON.parse(this.value)
    }catch(e) {
      json = {}
    }
    this._elValueElement.chart = new Chart(this._elValueElement, json)
  }

  rendertypeChanged(value, old) {
    if(this._elValueElement) this._elValueElement.rendertype = value || PROPERTIES.rendertype?.default
  } // rendertypeChanged
} // class meleInput

meleInput._properties = PROPERTIES
meleInput.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-input', meleInput)
